import { Card, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { getMemberCollectionList } from 'apis/ums';
import usePagination from 'commons/hooks/usePagination';
import LOCALS from 'commons/locals';
import { useCallback, useEffect, useMemo } from 'react';
import { Trans } from 'react-i18next';
import { PageQuery, UnwrapPromise } from 'types/base';
import { UmsMember } from 'types/ums';
import formatTime from 'utils/formatTime';
import {
  PMS_PRODUCT_STOCK_STATUS_OPTION_LIST,
  findLabelByValue,
} from 'commons/options';
import showTotal from 'components/show-total';
import LinkButton from 'components/link-button';
import { Heart } from 'lucide-react';
import { Image } from 'antd';
import { STOCK_PLACE_OPTION_LIST } from 'constant/stock-place';

type MemberCollectionListProps = {
  memberId: UmsMember['id'];
};

type MemberCollectionItem = UnwrapPromise<
  ReturnType<typeof getMemberCollectionList>
>['list'][number];

const MemberCollectionList = ({ memberId }: MemberCollectionListProps) => {
  const {
    loading,
    setLoading,
    pageNum,
    setPageNum,
    pageSize,
    setPageSize,
    total,
    setTotal,
    dataSource,
    setDataSource,
  } = usePagination<MemberCollectionItem>();

  const getDataSource = useCallback(
    async ({ pageNum, pageSize }: PageQuery) => {
      try {
        setLoading(true);

        const { list, total } = await getMemberCollectionList({
          memberId,
          pageNum,
          pageSize,
        });

        setDataSource(list);
        setTotal(total);
      } catch (err) {
      } finally {
        setLoading(false);
      }
    },
    [memberId, setDataSource, setLoading, setTotal]
  );

  useEffect(() => {
    getDataSource({ pageNum: 1, pageSize: 10 });
  }, [getDataSource]);

  const getColumns = useCallback(() => {
    return [
      {
        title: <Trans i18nKey={LOCALS.sales_product_id}></Trans>,
        dataIndex: 'product',
        key: 'product-id',
        render: (product: MemberCollectionItem['product']) => {
          const href = `/pms/product-edit/${product.id}`;
          return <LinkButton href={href}>{product.id}</LinkButton>;
        },
      },
      {
        title: <Trans i18nKey={LOCALS.image}></Trans>,
        dataIndex: 'product',
        key: 'image',
        render: (product: MemberCollectionItem['product']) => {
          if (product.pic) {
            return <Image width={48} src={product.pic} />;
          }
          return '-';
        },
      },
      {
        title: <Trans i18nKey={LOCALS.product_name}></Trans>,
        dataIndex: 'product',
        key: 'product-name',
        render: (product: MemberCollectionItem['product']) => {
          return product.name || '-';
        },
      },
      {
        title: <Trans i18nKey={LOCALS.price}></Trans>,
        dataIndex: 'product',
        key: 'price',
        render: (product: MemberCollectionItem['product']) => {
          return `${
            product.currency || 'JPY'
          } ${product.price.toLocaleString()}`;
        },
      },
      {
        title: <Trans i18nKey={LOCALS.stock_place}></Trans>,
        dataIndex: 'product',
        key: 'stock-place',
        render: (product: MemberCollectionItem['product']) => {
          return findLabelByValue(product.stockPlace, STOCK_PLACE_OPTION_LIST);
        },
      },
      {
        title: <Trans i18nKey={LOCALS.uMihwkqqts}></Trans>,
        dataIndex: 'product',
        key: 'stock-status',
        render: (product: MemberCollectionItem['product']) => {
          return product.stockStatus
            ? findLabelByValue(
                product.stockStatus,
                PMS_PRODUCT_STOCK_STATUS_OPTION_LIST
              )
            : '-';
        },
      },
      {
        title: <Trans i18nKey={LOCALS.stock}></Trans>,
        dataIndex: 'product',
        key: 'stock',
        render: (product: MemberCollectionItem['product']) => {
          return product.stock ?? '-';
        },
      },
      {
        title: <Trans i18nKey={LOCALS.created_time}></Trans>,
        dataIndex: 'createTime',
        key: 'createTime',
        render: (createTime: MemberCollectionItem['createTime']) => {
          return formatTime(createTime);
        },
      },
    ];
  }, []);

  const columns: ColumnsType<MemberCollectionItem> = useMemo(
    () => getColumns(),
    [getColumns]
  );

  if (dataSource.length === 0) {
    return null;
  }

  return (
    <Card bodyStyle={{ padding: '16px' }}>
      <div className="flex items-center gap-2 mb-4 text-base font-medium">
        <Heart className="w-5 h-5" />
        <Trans i18nKey={LOCALS.collections} />
      </div>
      <Table
        bordered
        pagination={{
          showTotal,
          total,
          pageSize,
          current: pageNum,
          onChange: (page, pageSize) => {
            setPageNum(page);
            setPageSize(pageSize);
            getDataSource({ pageNum: page, pageSize });
          },
        }}
        loading={loading}
        rowKey={'id'}
        tableLayout="auto"
        scroll={{ x: 'max-content' }}
        dataSource={dataSource}
        columns={columns}
      />
    </Card>
  );
};

export default MemberCollectionList;
